import React, { useEffect, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { parseHtmlContent } from '../../../../../../MyActivities/MyCV/helpers/ProcessTemplate';
import { getDefaultLevelId } from '../../../../../../../constants/skillLevels';
import { getTemplateTypeConstant, MATRIX_TEMPLATE_TYPES } from '../../../../../../../constants/matrixTemplateTypes';
import { REVIEW_TYPES } from '../../../../../../../constants/reviewTypes';
import { MIN_COMMENTS_LENGTH } from '../../../../../../../constants/assignmentConstants';
import ReviewerCompetency from '../ReviewerCompetency';
import ReviewerCompetencyRadio from '../ReviewerCompetencyRadio';
import { CommentsField, DirtyStateContext } from './CommentsField';
import DiscrepancyAnalysis from '../DiscrepancyAnalysis';

// Component to handle reviewer competency display
const ReviewerCompetencyList = ({ 
  skillId, 
  segmentId = null, 
  competencies, 
  employeeId, 
  visualizationType, 
  isInteractive = true,
  skillName = null,
  customDescriptions = null,
  isLoadingDescriptions = false,
  onLevelSelect = null,
  onDropdownClick = null,
  skillLevels,
  commentsAllowed = false,
  commentsRequired = true,
  onCommentChange = null,
  loadingSegmentMap = {},
  loadingOverallSkill = {},
  followUpQuestions = [],
  assignment,
  hasMappings = false,
}) => {
  const { isDirty, setIsDirty, savingFieldId, setSavingFieldId } = useContext(DirtyStateContext);
  const defaultLevelId = getDefaultLevelId(skillLevels);

  // Early return if assignment is not provided
  if (!assignment) {
    return null;
  }

  // Extract reviewers from assignment
  const reviewers = assignment.reviewers || [];

  useEffect(() => {
    // Check if any segment or overall skill is currently loading
    const isLoading = Object.values(loadingSegmentMap).some(loading => loading === true) || 
                      Object.values(loadingOverallSkill).some(loading => loading === true);
    setIsDirty(isLoading);
  }, [loadingSegmentMap, loadingOverallSkill, setIsDirty]);

  const reviewerComponents = reviewers
    .sort((a, b) => a.order - b.order)
    .map(reviewer => {

      const reviewType = reviewer.reviewType;
      const reviewTemplateType = getTemplateTypeConstant(reviewer.skillsMatrixTemplate.type);
      const isParallelReview = reviewType === REVIEW_TYPES.PARALLEL;
      const isSequentialReview = reviewType === REVIEW_TYPES.SEQUENTIAL;
      // Determine if we're showing segment or overall competency
      const competencyLevelId = segmentId 
        ? competencies.reviewers[reviewer.id]?.skills[skillId]?.segments?.[segmentId]?.competencyLevelId || defaultLevelId
        : competencies.reviewers[reviewer.id]?.skills[skillId]?.overall || defaultLevelId;

      const isCurrentReviewer = competencies.reviewers[reviewer.id]?.isCurrent;
      const isSelfAssessment = reviewer.employee.id === employeeId;
      
      // Use separate variables for dropdown/radio interactivity and comments interactivity
      const shouldBeInteractiveForDropdown = isInteractive && isCurrentReviewer && isSelfAssessment && !reviewer.completedAt;
      const shouldBeInteractiveForComments = isInteractive && isCurrentReviewer && isSelfAssessment && !reviewer.completedAt;
      // Show the follow up questions if the reviewer has not completed the assessment and only for the current reviewer
      const shouldShowFollowUpQuestions = isInteractive && isCurrentReviewer && isSelfAssessment && !reviewer.completedAt && followUpQuestions && followUpQuestions.length > 0;
      // Determine if skill levels should be shown based on review type and conditions
      let shouldShowSkillLevels = true; // Default for non-sequential reviews
      let shouldShowComments = true;
      
      if (isSequentialReview) {
        if (reviewer.order === 1 && reviewers.length > 1) {
          // For self-assessment (order 1) with multiple reviewers, show only if next reviewer (order 2) has completed
          const nextReviewer = reviewers.find(r => r.order === 2);
          shouldShowSkillLevels = nextReviewer?.completedAt ? true : false;
        } else if (reviewer.order === 1 && reviewers.length === 1 && isSelfAssessment) {
          // Single reviewer self-assessment
          shouldShowSkillLevels = true;
        } else if (reviewer.order > 1) {
          // For all other reviewers (order > 1), always show skill levels
          shouldShowSkillLevels = true;
        } else {
          // Default case for other conditions
          shouldShowSkillLevels = false;
        }
      }

      if (hasMappings) {
        shouldShowSkillLevels = false;
        shouldShowComments = false;
      }
      
      const reviewerComments = segmentId ? 
        competencies.reviewers[reviewer.id]?.skills[skillId]?.segments?.[segmentId]?.comments || ''
        : competencies.reviewers[reviewer.id]?.skills[skillId]?.comments || '';
      
      // Skip reviewers who haven't provided this competency yet, except for current reviewer
      // For sequential reviews, always show self-assessment to the employee
      if (!isCurrentReviewer && (!reviewer.completedAt || !competencyLevelId)) {
        return null;
      }

      /*
      if (isSequentialReview && !isCurrentReviewer) {
        return null;
      }
        */

      if (isParallelReview && !isCurrentReviewer) {
        return null;
      }

      const additionalInfo = isParallelReview 
        ? reviewTemplateType === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT 
          ? 'Self Assessment' 
          : 'Evaluator' 
        : reviewer.order === 1 
          ? 'Self Assessment' 
          : `Reviewer ${reviewer.order}`;

      // Determine the loading state based on whether we're showing segment or overall
      const isLoading = segmentId 
        ? loadingSegmentMap[`${reviewer.id}-${segmentId}`] 
        : loadingOverallSkill[`${reviewer.id}-${skillId}`];

      // Check if we should show DiscrepancyAnalysis before this reviewer
      const isLastReviewer = reviewer.order === assignment?.lastReviewerOrder;
      
      return (
        <React.Fragment key={`reviewer-competency-fragment-${reviewer.id}-${skillId}`}>
          {/* Show Discrepancy Analysis before the last reviewer */}
          {isLastReviewer && (
            <DiscrepancyAnalysis
              skillId={skillId}
              segmentId={segmentId}
              competencies={competencies}
              skillLevels={skillLevels}
              skillName={skillName}
              assignment={assignment}
              reviewer={reviewer}
            />
          )}
          
          <Box key={`reviewer-competency-${reviewer.id}-${skillId}`} sx={{ mb: isCurrentReviewer ? 2 : 1 }}>
            {visualizationType === 'dropdown' ? (
              <ReviewerCompetency
                key={`reviewer-competency-${reviewer.id}-${skillId}`}
                reviewer={reviewer}
                competencyLevelId={competencyLevelId}
                showDropdownArrow={shouldBeInteractiveForDropdown && !isDirty}
                onClick={shouldBeInteractiveForDropdown && onDropdownClick ? (e) => onDropdownClick(e, reviewer, segmentId) : undefined}
                isInteractive={shouldBeInteractiveForDropdown && !isDirty}
                opacity={(shouldBeInteractiveForDropdown || isParallelReview) && !isDirty ? 1 : 0.6}
                additionalInfo={additionalInfo}
                skillLevels={skillLevels}
                loading={isLoading}
                showSkillLevels={shouldShowSkillLevels}

              />
            ) : (
              <ReviewerCompetencyRadio
                key={`reviewer-competency-radio-${reviewer.id}-${skillId}`}
                reviewer={reviewer}
                competencyLevelId={competencyLevelId}
                onLevelSelect={shouldBeInteractiveForDropdown && onLevelSelect ? (level) => onLevelSelect(reviewer, skillId, segmentId, level) : undefined}
                isInteractive={shouldBeInteractiveForDropdown && !isDirty}
                opacity={(shouldBeInteractiveForDropdown || isParallelReview) && !isDirty ? 1 : 0.6}
                additionalInfo={additionalInfo}
                skillName={skillName}
                customDescriptions={customDescriptions}
                isLoadingDescriptions={isLoadingDescriptions}
                skillLevels={skillLevels}
                limitedView={visualizationType === 'radio-limited'}
                loading={isLoading}
                showSkillLevels={shouldShowSkillLevels}
              />
            )}
          </Box>

          {commentsAllowed && (
            <Box key={`reviewer-comments-${reviewer.id}-${skillId}`}
              sx={{
                mt: 3,
                mb: 3,
                //minWidth: '500px',
                //width: visualizationType === 'dropdown' ? '50%' : 'auto'
              }}>

              {/* Follow-up Questions Section */}
              {shouldShowFollowUpQuestions && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Follow-up Questions for {skillName}
                  </Typography>
                  {followUpQuestions
                    .sort((a, b) => a.order - b.order)
                    .map((question, index) => (
                      <Box 
                        key={question.id}
                        sx={{ 
                          mb: 2,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          backgroundColor: '#f9f9f9'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: '#333' }}>
                          {index + 1}. {question.question}
                        </Typography>
                        {question.instructions && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-wrap',
                              display: 'block'
                            }}
                          >
                            {parseHtmlContent(question.instructions)}
                          </Typography>
                        )}
                      </Box>
                    ))
                  }
                </Box>
              )}

              {shouldShowComments && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Additional Comments for {skillName}
                  </Typography>
                  {shouldBeInteractiveForComments ? (
                    <CommentsField
                      initialComments={reviewerComments}
                      onCommentChange={(comments) => onCommentChange && onCommentChange(reviewer.id, skillId, comments)}
                      disabled={competencyLevelId === defaultLevelId && !hasMappings}
                      skillId={skillId}
                      reviewerId={reviewer.id}
                      required={commentsRequired}
                      minContentsLength={MIN_COMMENTS_LENGTH}
                      fieldId={segmentId ? `${reviewer.id}-${skillId}-${segmentId}` : `${reviewer.id}-${skillId}`}
                    />
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: '#f9f9f9',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        p: 2,
                        minHeight: '40px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        position: 'relative',
                        fontStyle: reviewerComments && reviewerComments.trim() ? 'normal' : 'italic',
                        color: reviewerComments && reviewerComments.trim() ? 'text.primary' : 'text.secondary',
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit',
                        opacity: isDirty ? 0.7 : 1,
                      }}
                    >
                      {(reviewerComments && reviewerComments.trim()) ? reviewerComments : 'No comments provided'}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </React.Fragment>
      );
    });

  return (
    <>
      {reviewerComponents}
    </>
  );
};

export default ReviewerCompetencyList; 